// MultiSelectToolbar — 다중 선택 상태의 floating 액션 툴바 (U44).
//
// 선택 항목이 2개 이상일 때 화면 하단(모바일)/하단 고정(데스크탑) 영역에 표시.
// 툴바 액션:
//   - 선택 N개 라벨
//   - [태그]    — DropdownMenu: 기존 태그 목록 + 새 태그 입력 → 일괄 addTag
//   - [이동]    — DropdownMenu: 그룹 트리 → 일괄 group_id 변경
//   - [핀]      — 일괄 pin
//   - [핀 해제] — 일괄 unpin
//   - [삭제]    — 일괄 삭제 (Undo 토스트는 U53에서 실제 구현)
//   - [해제]    — clear()
//
// 액션 실패 시 useToast.push({ variant: 'error' }) 표시.
// 선택 2개 미만이면 null 반환(렌더 안 함).

import { useState, useRef, useEffect, useCallback } from 'react';
import { useItemMutations } from '../hooks/useItems';
import { useTags } from '../hooks/useTags';
import { useGroups } from '../hooks/useGroups';
import { useToast } from '../ui/Toast';
import { formatApiError } from '../api/client';
import { useContext } from 'react';
import { UndoContext } from '../hooks/useUndoContext';
import { itemsApi } from '../api/items';

// ─── Props ────────────────────────────────────────────────────

interface MultiSelectToolbarProps {
  /** 현재 선택된 항목 id Set */
  selected: Set<number>;
  /** 선택 카운트 */
  selectedCount: number;
  /** 선택 전체 해제 콜백 */
  onClear: () => void;
  /** 삭제 후 콜백 (목록 갱신·애니메이션용) */
  onDeleted?: (ids: number[]) => void;
}

// ─── MultiSelectToolbar ────────────────────────────────────────

export function MultiSelectToolbar({
  selected,
  selectedCount,
  onClear,
  onDeleted,
}: MultiSelectToolbarProps) {
  // 선택 2개 미만이면 렌더 안 함
  if (selectedCount < 2) return null;

  return (
    <div
      className="multiselect-toolbar"
      role="toolbar"
      aria-label={`${selectedCount}개 항목 선택됨`}
    >
      <span className="multiselect-toolbar__count">
        {selectedCount}개 선택
      </span>

      <div className="multiselect-toolbar__actions">
        {/* 태그 드롭다운 */}
        <TagDropdown selected={selected} />

        {/* 이동 드롭다운 */}
        <MoveDropdown selected={selected} />

        {/* 핀 토글 */}
        <PinAction selected={selected} />

        {/* 삭제 */}
        <DeleteAction selected={selected} onDeleted={onDeleted} onClear={onClear} />

        {/* 선택 해제 */}
        <button
          type="button"
          className="btn btn--ghost multiselect-toolbar__clear"
          onClick={onClear}
          aria-label="선택 해제"
        >
          해제
        </button>
      </div>
    </div>
  );
}

// ─── TagDropdown — 태그 일괄 추가 ─────────────────────────────

function TagDropdown({ selected }: { selected: Set<number> }) {
  const [open, setOpen] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const { addItemTag } = useItemMutations();
  const { data: tags = [] } = useTags('', 100);
  const { push: toastPush } = useToast();
  const menuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const applyTag = useCallback(async (tagName: string) => {
    const trimmed = tagName.trim();
    if (!trimmed) return;
    setOpen(false);
    setNewTagInput('');

    // 병렬로 모든 선택 항목에 태그 추가
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) => addItemTag.mutateAsync({ id, name: trimmed }))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      toastPush({ title: `${failed}개 항목 태그 추가 실패`, variant: 'error' });
    } else {
      toastPush({ title: `${ids.length}개 항목에 태그 "${trimmed}" 추가`, variant: 'success' });
    }
  }, [selected, addItemTag, toastPush]);

  return (
    <div className="multiselect-toolbar__dropdown" ref={menuRef}>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        태그
      </button>
      {open && (
        <div className="multiselect-toolbar__menu" role="menu">
          {/* 새 태그 입력 */}
          <div className="multiselect-toolbar__menu-input">
            <input
              type="text"
              placeholder="새 태그 입력 후 Enter"
              value={newTagInput}
              onChange={(e) => setNewTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTagInput.trim()) {
                  e.preventDefault();
                  applyTag(newTagInput);
                }
                if (e.key === 'Escape') setOpen(false);
              }}
              autoFocus
            />
          </div>
          {/* 기존 태그 목록 */}
          <div className="multiselect-toolbar__menu-list">
            {tags.length === 0 && (
              <span className="multiselect-toolbar__menu-empty muted">태그 없음</span>
            )}
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="multiselect-toolbar__menu-item"
                role="menuitem"
                onClick={() => applyTag(tag.name)}
              >
                {tag.color && (
                  <span
                    className="tag-dot"
                    style={{ background: tag.color }}
                    aria-hidden="true"
                  />
                )}
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MoveDropdown — 그룹 이동 ─────────────────────────────────

function MoveDropdown({ selected }: { selected: Set<number> }) {
  const [open, setOpen] = useState(false);
  const { updateItem } = useItemMutations();
  const { data: groups = [] } = useGroups();
  const { push: toastPush } = useToast();
  const menuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const moveToGroup = useCallback(async (groupId: number | null) => {
    setOpen(false);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) => updateItem.mutateAsync({ id, patch: { group_id: groupId } }))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      toastPush({ title: `${failed}개 항목 이동 실패`, variant: 'error' });
    } else {
      const groupName = groupId === null
        ? '루트'
        : groups.find((g) => g.id === groupId)?.name ?? `그룹 ${groupId}`;
      toastPush({ title: `${ids.length}개 항목을 "${groupName}"으로 이동`, variant: 'success' });
    }
  }, [selected, updateItem, groups, toastPush]);

  return (
    <div className="multiselect-toolbar__dropdown" ref={menuRef}>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        이동
      </button>
      {open && (
        <div className="multiselect-toolbar__menu" role="menu">
          {/* 루트 이동 */}
          <button
            type="button"
            className="multiselect-toolbar__menu-item"
            role="menuitem"
            onClick={() => moveToGroup(null)}
          >
            루트 (그룹 없음)
          </button>
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              className="multiselect-toolbar__menu-item"
              role="menuitem"
              onClick={() => moveToGroup(group.id)}
            >
              {group.icon && <span aria-hidden="true">{group.icon} </span>}
              {group.name}
            </button>
          ))}
          {groups.length === 0 && (
            <span className="multiselect-toolbar__menu-empty muted">그룹 없음</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PinAction — 일괄 핀 토글 ─────────────────────────────────

function PinAction({ selected }: { selected: Set<number> }) {
  const { pinItem, unpinItem } = useItemMutations();
  const { push: toastPush } = useToast();

  const handlePin = useCallback(async () => {
    const ids = Array.from(selected);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => pinItem.mutateAsync(id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        toastPush({ title: `${failed}개 항목 핀 실패`, variant: 'error' });
      } else {
        toastPush({ title: `${ids.length}개 항목 핀 설정`, variant: 'success' });
      }
    } catch (err) {
      toastPush({ title: formatApiError(err, '핀 설정 실패'), variant: 'error' });
    }
  }, [selected, pinItem, toastPush]);

  const handleUnpin = useCallback(async () => {
    const ids = Array.from(selected);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => unpinItem.mutateAsync(id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        toastPush({ title: `${failed}개 항목 핀 해제 실패`, variant: 'error' });
      } else {
        toastPush({ title: `${ids.length}개 항목 핀 해제`, variant: 'success' });
      }
    } catch (err) {
      toastPush({ title: formatApiError(err, '핀 해제 실패'), variant: 'error' });
    }
  }, [selected, unpinItem, toastPush]);

  return (
    <span className="multiselect-toolbar__pin-group">
      <button
        type="button"
        className="btn btn--ghost"
        onClick={handlePin}
        aria-label="선택 항목 핀 설정"
        title="핀 설정"
      >
        핀
      </button>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={handleUnpin}
        aria-label="선택 항목 핀 해제"
        title="핀 해제"
      >
        핀 해제
      </button>
    </span>
  );
}

// ─── DeleteAction — 일괄 삭제 (Undo 토스트는 U53) ─────────────

interface DeleteActionProps {
  selected: Set<number>;
  onDeleted?: (ids: number[]) => void;
  onClear: () => void;
}

function DeleteAction({ selected, onDeleted, onClear }: DeleteActionProps) {
  const { deleteItem } = useItemMutations();
  const { push: toastPush } = useToast();
  // UndoContext — 멀티 삭제 일괄 undo 등록
  const undoCtx = useContext(UndoContext);

  const handleDelete = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    try {
      // deleteItem mutationFn은 { id, name? } 인터페이스로 호출
      const results = await Promise.allSettled(
        ids.map((id) => deleteItem.mutateAsync({ id }))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = ids.length - failed;

      if (failed > 0) {
        toastPush({ title: `${failed}개 항목 삭제 실패 (${succeeded}개 성공)`, variant: 'error' });
      } else {
        // 성공한 ids에 대해 일괄 undo 등록
        if (undoCtx && ids.length > 0) {
          undoCtx.push({
            description: `${ids.length}개 항목 삭제됨`,
            undo: async () => {
              // Promise.allSettled — 개별 복원 실패 격리
              await Promise.allSettled(
                ids.map((id) => itemsApi.restoreItem(id))
              );
            },
          });
        } else {
          // UndoContext 없을 때는 기존 success 토스트 (폴백)
          toastPush({ title: `${ids.length}개 항목 삭제됨`, variant: 'success' });
        }
        onDeleted?.(ids);
        onClear();
      }
    } catch (err) {
      toastPush({ title: formatApiError(err, '삭제 실패'), variant: 'error' });
    }
  }, [selected, deleteItem, toastPush, undoCtx, onDeleted, onClear]);

  return (
    <button
      type="button"
      className="btn btn--ghost btn--danger"
      onClick={handleDelete}
      aria-label={`선택된 ${selected.size}개 항목 삭제`}
      title="삭제"
    >
      삭제
    </button>
  );
}
